import XlsDownload from "@dana/Icons/XlsDownload";
import { elevation, themeColors } from "@dana/MobiusOverrides/styles/colors";
import Icon from "@insite/mobius/Icon";
import * as XLSX from "xlsx-js-style";
import React, { forwardRef } from "react";
import styled from "styled-components";
import { InvoiceDetailsResponse } from "@dana/Types/ApiModels";
import { formatDateRegion, formatDateWithDash } from "@dana/Common/utils";
import translate from "@insite/client-framework/Translate";
import { EMEARegion } from "@dana/Common/Constants";
import { formatDecimal } from "@dana/utils/utils";

type CommercialExcelDownloadButtonProps = {
    invoice?: InvoiceDetailsResponse | null;
    totalAmt?:number;    
    totalDiscount?:number;
    totalCertiCost?:number;
    totalPrice?:number;
    weightUnit?:string;
    totalWeight?:number;
    miscCharges?:number;
    fileName?: string;
    sheetName?: string;
    label?: string;
    size?: number;
    disabled?: boolean;
    className?: string;
    selectedRegion?:string;
    onBeforeDownload?: () => void | Promise<void>;
    onAfterDownload?: () => void;
};

const StyledExcelDownloadButton = styled.button`
    padding: 4px;
    background-color: transparent;
    border: none;
    cursor: pointer;

    &:hover {
        color: ${themeColors.secondary.danaDarkBlue};
    }

    &:focus-visible {
        color: ${themeColors.secondary.danaDarkBlue};
        border: 1px solid ${themeColors.primary.white};
        box-shadow: ${elevation.focus};
    }

    @media print {
        display: none;
    }
`;

const NUM_FMT = "0.00";

function applyAlignment(ws: XLSX.WorkSheet, cellAddress: string, horizontal: "right" | "left" | "center" = "right") {
    if (ws[cellAddress]) {
        ws[cellAddress].s = {
            ...(ws[cellAddress].s ?? {}),
            alignment: { horizontal },
        };
    }
}

function applyNumberFormat(ws: XLSX.WorkSheet, cellAddress: string) {
    if (ws[cellAddress]) {
        ws[cellAddress].z = NUM_FMT;
        applyAlignment(ws, cellAddress, "right");
    }
}

function buildInvoiceWorkbook(invoice: InvoiceDetailsResponse | null, 
    totalAmt:number,
    totalDiscount:number,
    totalCertiCost:number,
    totalPrice:number,
    weightUnit:string,
    totalWeight: number,
    miscCharges:number,
    fileName: string, sheetName: string, selectedRegion:string) {
    const rows: (string | number | null)[][] = [
        [translate("CUSTOMER"), invoice?.mailName ??""],      
        [translate("INVOICE NO."), invoice?.invoiceNumber ?? ""],
        [translate("DATE OF EXPORT"), formatDateRegion(selectedRegion,invoice?.documentDate?.split('T')[0] || "")],
        [translate("TERMS OF PAYMENT"), invoice?.termsDescription ??""],
        [translate("B/L, AWB NO."), invoice?.deliveryNumber ??""],
        [],
        [],
        [
            translate("Item"),
            translate("Dana Part Number"),
            translate("Customer PO"),
            translate("Order Number"),
            translate("Customer Part Number"),
            translate("Part Description"),
            translate("Country of Origin"),
            translate("H.S. Number"),
            `${translate("Item Weight")} (${(weightUnit)})`,
            `${translate("Unit Price In")} ${invoice?.currency ?? ""}`,
            `${translate("3.1 Certificate Cost")} (${invoice?.currency ?? ""})`,
            `${translate("Value In")} ${invoice?.currency ?? ""}`,
        ],
       ...(invoice?.invoiceInfo?.map(item => [
        item.lineNumber,
    item.partNumber,
    item.poNumber,
    item.orderNumber,
    item.customerPartNumber,
    item.partDescription,
    item.countryOfOrigin,
    item.hsNumber,
    selectedRegion === EMEARegion ? formatDecimal(item.weight, { locale: "fr-FR" }):item.weight,
    selectedRegion === EMEARegion ? formatDecimal(item.pricePerUnit, { locale: "fr-FR" }):0,
    selectedRegion === EMEARegion ? formatDecimal(item.partPriceInfo?.find(p => p.priceType === "3.1 Certification")?.amount ?? 0,{ locale: "fr-FR" }):0,
    selectedRegion === EMEARegion ? formatDecimal((item.netAmount ?? 0) , { locale: "fr-FR" }) : formatDecimal(item.netAmount),
]) ?? []),
        [],
        [null, null,null,null, null, null, null, null, null, null, `${translate("Total Weight")} (${weightUnit ?? ""}):`, selectedRegion === EMEARegion ? formatDecimal(totalWeight.toFixed(2), { locale: "fr-FR" }):totalWeight.toFixed(2)],
        [null, null,null,null, null, null, null, null, null, null,`${translate("Line Total")} (${invoice?.currency ?? ""}):`, selectedRegion === EMEARegion ? formatDecimal(totalPrice, { locale: "fr-FR" }) : formatDecimal(totalPrice)],       
        [null, null,null,null, null, null, null, null, null, null, `${translate("Total 3.1 Certificate Cost")} (${invoice?.currency ?? ""}):`, formatDecimal(totalCertiCost ?? 0, { locale: "fr-FR" })],
        [null, null,null,null, null, null, null, null, null, null, `${translate("MISC CHARGES (Packing, Freight, etc.)")} (${invoice?.currency ?? ""}):`,selectedRegion === EMEARegion ? formatDecimal(miscCharges?? 0, { locale: "fr-FR" }) : formatDecimal(miscCharges ?? 0)],
        [null, null,null,null, null, null, null, null, null, null, `${translate("Discount")} (${invoice?.currency ?? ""}):`, selectedRegion === EMEARegion ? `-${formatDecimal(totalDiscount,{ locale: "fr-FR" })}`: formatDecimal(totalDiscount)],       
        [null, null,null,null, null, null, null, null, null, null, `${translate("Invoice Total")} (${invoice?.currency ?? ""}):`, selectedRegion === EMEARegion ? formatDecimal(totalAmt, { locale: "fr-FR" }):formatDecimal(totalAmt)],
    ];

    const ws = XLSX.utils.aoa_to_sheet(rows);

   ws["!cols"] = [
    { wch: 18 },  // A - Item
    { wch: 18 },  // B - Dana Part Number
    { wch: 18 },  // B - Dana Part Number
    { wch: 18 },  // B - Dana Part Number
    { wch: 20 },  // C - Customer Part Number
    { wch: 30 },  // D - Part Description
    { wch: 18 },  // E - Country of Origin
    { wch: 14 },  // F - H.S. Number
    { wch: 16 },  // G - Item Weight
    { wch: 16 },  // H - Unit Price
    { wch: 28 },  // I - 3.1 Certificate Cost / totals labels
    { wch: 16 },  // J - Value / totals values
];
   
    const lineItemStartRow = 9; // row 10 = first data row (1-indexed, after 9 header rows)
    invoice?.invoiceInfo?.forEach((_, i) => {
        const rowNum = lineItemStartRow + i;
        applyNumberFormat(ws, `I${rowNum}`);
        applyNumberFormat(ws, `J${rowNum}`);
        applyNumberFormat(ws, `K${rowNum}`);
        applyNumberFormat(ws, `L${rowNum}`);
    });

    // Totals block numeric column (G), 5 rows starting after the blank spacer row
    const totalsStartRow = lineItemStartRow + (invoice?.invoiceInfo?.length ?? 0) + 1;
    for (let i = 0; i < 6; i++) {
        applyNumberFormat(ws, `J${totalsStartRow + i}`);
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${fileName}.xlsx`);
}

export const CommercialExcelDownloadButton = forwardRef<HTMLButtonElement, CommercialExcelDownloadButtonProps>(
    (
        {
            invoice,
            totalAmt,
            totalCertiCost,
            totalDiscount,
            totalPrice,
            weightUnit,
            totalWeight,
            miscCharges,
            fileName = "commercial invoice",
            sheetName = "Commercial Invoice",
            label = "Download Excel",
            size = 28,
            disabled,
            className,
            selectedRegion,
            onBeforeDownload,
            onAfterDownload,
        },
        ref,
    ) => {
        const handleDownload = async () => {
            if (disabled) return;
            if (typeof window === "undefined") return;

            try {
                await onBeforeDownload?.();
            } catch {
                // Swallow to avoid blocking download
            }

            try {
                buildInvoiceWorkbook(invoice ?? null,totalAmt ??0,totalDiscount??0,totalCertiCost??0,totalPrice??0, weightUnit??"", totalWeight??0,miscCharges??0, fileName, sheetName,selectedRegion ??"");
            } finally {
                onAfterDownload?.();
            }
        };

        return (
            <StyledExcelDownloadButton
                ref={ref}
                type="button"
                aria-label={label}
                title={label}
                onClick={handleDownload}
                className={className}
                disabled={disabled}
            >
                <Icon src={XlsDownload} size={size} />
            </StyledExcelDownloadButton>
        );
    },
);

CommercialExcelDownloadButton.displayName = "CommercialExcelDownloadButton";